import * as THREE from 'three';
import type { SimCar } from '../../sim/traffic';
import { type AudioEngine, type LoopVoice, ease } from './audioEngine';
import { type ListenerEnv, Gearbox, Strides, ambienceMix, assignVoices, doppler, stepSurface } from './mixLogic';
import { horn } from './synth';

/** What the audio needs from the player each frame (navigation.ts). */
export interface PlayerAudio {
  walker: { feet: THREE.Vector3; grounded: boolean; running: boolean } | null;
  car: {
    position: THREE.Vector3;
    speed: number;
    throttle: number;
    handbrake: boolean;
    /** Sideways speed (m/s): skidding. */
    lateral: number;
    /** Wheels touching the ground. */
    wheels: number;
    /** The hardest hit (m/s of velocity change) since the last read. */
    impact: number;
  } | null;
  /** The collider tag and height of the ground under a point. */
  ground: (p: THREE.Vector3) => { tag: string | undefined; y: number } | null;
}

/** Engine voices for the nearest traffic cars, per quality preset. */
export const VOICES = { low: 3, mid: 6, high: 8 };
/** Traffic cars farther than this (m) are left to the city bed. */
const VOICE_RANGE = 75;
/** The player car engine loop was recorded at about this engine speed (its firing frequency, 67 Hz). */
const ENGINE_RPM = 2000;

interface TrafficVoice {
  out: GainNode;
  panner: PannerNode;
  engine: LoopVoice;
  engineFilter: BiquadFilterNode;
  tyre: GainNode;
  tyreFilter: BiquadFilterNode;
  car: number | null;
  /** While switching cars: fade out, then move and fade in. */
  switching: number;
  pending: number | null;
  lastDist: number;
  closing: number;
  lastV: number;
  accel: number;
}

interface PlayerCarVoice {
  panner: PannerNode;
  engine: LoopVoice;
  filter: BiquadFilterNode;
  growl: OscillatorNode;
  growlSub: OscillatorNode;
  growlGain: GainNode;
  growlFilter: BiquadFilterNode;
  road: GainNode;
  roadFilter: BiquadFilterNode;
  squeal: LoopVoice;
}

const hashId = (id: number) => {
  let h = Math.imul(id ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  return ((Math.imul(h, 0xc2b2ae35) ^ (h >>> 16)) >>> 0) / 4294967296;
};

/**
 * The city's sound (DECISIONS "Audio"): ambience beds mixed by where the listener is (street, height,
 * parks, indoors), engine and tyre voices on the nearest traffic cars with Doppler, horns from cars
 * the player blocks (and the odd impatient toot), distant sirens now and then, the player's car
 * (engine through an automatic gearbox, road roar, tyre squeal, crashes) and footsteps by surface.
 */
export class CityAudio {
  private beds: Record<'city' | 'air' | 'birds' | 'room', LoopVoice | null> | null = null;
  private wind: { gain: GainNode; filter: BiquadFilterNode } | null = null;
  private voices: TrafficVoice[] = [];
  private player: PlayerCarVoice | null = null;
  /** The player car's gearbox (also shown on the speedometer); runs with or without sound. */
  readonly gearbox = new Gearbox();
  private readonly strides = new Strides();
  private lastFeet: THREE.Vector3 | null = null;
  private readonly lastStep = new Map<string, number>();
  private readonly hornAt = new Map<number, number>();
  private nextToot = 20;
  private nextSiren = 40 + Math.random() * 60;
  private siren: { panner: PannerNode; angle: number; dist: number; until: number } | null = null;
  private lastHit = 0;
  private time = 0;
  private windPhase = 0;
  voiceCount = VOICES.mid;
  /** The last listener environment and mix (for the HUD and debugging). */
  last: { env: ListenerEnv; mix: ReturnType<typeof ambienceMix>; voiced: number } | null = null;

  constructor(private readonly audio: AudioEngine) {
    audio.onReady(() => this.start());
  }

  private start(): void {
    const a = this.audio;
    const ctx = a.ctx!;
    this.beds = {
      city: a.loop('city', a.outdoor),
      air: a.loop('air', a.outdoor),
      birds: a.loop('birds', a.outdoor),
      room: a.loop('room', a.direct),
    };
    // Wind from moving fast: noise through a slowly wandering band-pass.
    const src = a.noiseSource();
    if (src) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 500;
      filter.Q.value = 0.6;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(filter);
      filter.connect(gain);
      gain.connect(a.direct);
      this.wind = { gain, filter };
    }
  }

  /** A traffic voice: engine loop and tyre roar through one HRTF panner into the street bus. */
  private makeVoice(): TrafficVoice | null {
    const a = this.audio;
    const ctx = a.ctx!;
    const panner = a.panner(7, 1.1);
    if (!panner) return null;
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(panner);
    panner.connect(a.outdoor);
    panner.connect(a.streetSend);
    const engineFilter = ctx.createBiquadFilter();
    engineFilter.type = 'lowpass';
    engineFilter.Q.value = 0.7;
    engineFilter.connect(out);
    const engine = a.loop('engineIdle', engineFilter, 0.5);
    const tyreFilter = ctx.createBiquadFilter();
    tyreFilter.type = 'bandpass';
    tyreFilter.Q.value = 0.8;
    const tyre = ctx.createGain();
    tyre.gain.value = 0;
    const noise = a.noiseSource();
    if (!engine || !noise) return null;
    noise.connect(tyreFilter);
    tyreFilter.connect(tyre);
    tyre.connect(out);
    return { out, panner, engine, engineFilter, tyre, tyreFilter, car: null, switching: 0, pending: null, lastDist: 0, closing: 0, lastV: 0, accel: 0 };
  }

  private makePlayerCar(): PlayerCarVoice | null {
    const a = this.audio;
    const ctx = a.ctx!;
    const panner = a.panner(9, 1);
    if (!panner) return null;
    panner.connect(a.direct);
    panner.connect(a.streetSend);
    panner.connect(a.hallSend);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.8;
    filter.connect(panner);
    const engine = a.loop('engine', filter, 0);
    // Under load the engine growls: the firing frequency and its sub-harmonic, darkened.
    const growlFilter = ctx.createBiquadFilter();
    growlFilter.type = 'lowpass';
    growlFilter.Q.value = 1.2;
    const growlGain = ctx.createGain();
    growlGain.gain.value = 0;
    growlFilter.connect(growlGain);
    growlGain.connect(panner);
    const growl = ctx.createOscillator();
    growl.type = 'sawtooth';
    const growlSub = ctx.createOscillator();
    growlSub.type = 'triangle';
    growl.connect(growlFilter);
    growlSub.connect(growlFilter);
    growl.start();
    growlSub.start();
    const roadFilter = ctx.createBiquadFilter();
    roadFilter.type = 'lowpass';
    roadFilter.Q.value = 0.5;
    const road = ctx.createGain();
    road.gain.value = 0;
    const noise = a.noiseSource();
    noise?.connect(roadFilter);
    roadFilter.connect(road);
    road.connect(panner);
    const squeal = a.loop('squeal', panner, 0);
    if (!engine || !squeal) return null;
    return { panner, engine, filter, growl, growlSub, growlGain, growlFilter, road, roadFilter, squeal };
  }

  private dropPlayerCar(): void {
    const p = this.player;
    if (!p) return;
    const ctx = this.audio.ctx!;
    const t = ctx.currentTime;
    for (const g of [p.engine.gain.gain, p.growlGain.gain, p.road.gain, p.squeal.gain.gain]) g.setTargetAtTime(0, t, 0.05);
    const stopAt = t + 0.4;
    p.engine.src.stop(stopAt);
    p.squeal.src.stop(stopAt);
    p.growl.stop(stopAt);
    p.growlSub.stop(stopAt);
    setTimeout(() => p.panner.disconnect(), 600);
    this.player = null;
  }

  update(dt: number, camera: THREE.Camera, env: ListenerEnv, cars: readonly SimCar[], alpha: number, me: PlayerAudio): void {
    if (me.car) this.gearbox.update(dt, me.car.speed, me.car.throttle * (me.car.speed < -0.3 ? -1 : 1));
    const a = this.audio;
    const ctx = a.ctx;
    if (!ctx || !a.ready || ctx.state !== 'running') return;
    this.time += dt;
    a.setListener(camera);
    const cam = camera.getWorldPosition(new THREE.Vector3());
    this.ambience(dt, env);
    this.traffic(dt, cam, cars, alpha);
    this.horns(dt, cam, cars);
    this.sirens(dt, cam);
    this.playerCar(me);
    this.footsteps(dt, me);
  }

  private ambience(dt: number, env: ListenerEnv): void {
    const ctx = this.audio.ctx!;
    const m = ambienceMix(env);
    this.last = { env, mix: m, voiced: this.voices.filter((v) => v.car !== null).length };
    const b = this.beds!;
    if (b.city) ease(b.city.gain.gain, m.city * 0.9, ctx, 0.4);
    if (b.air) ease(b.air.gain.gain, m.air * 0.8, ctx, 0.4);
    if (b.birds) ease(b.birds.gain.gain, m.birds * 0.9, ctx, 0.8);
    if (b.room) ease(b.room.gain.gain, m.room * 0.7, ctx, 0.3);
    ease(this.audio.outdoorFilter.frequency, m.outdoorCutoff, ctx, 0.15);
    ease(this.audio.streetSend.gain, 0.16 * m.street, ctx, 0.3);
    ease(this.audio.hallSend.gain, 0.3 * m.hall, ctx, 0.3);
    if (this.wind) {
      // Gusts: the band wanders and the level breathes.
      this.windPhase += dt;
      const gust = 0.75 + 0.25 * Math.sin(this.windPhase * 0.7) * Math.sin(this.windPhase * 0.23 + 1);
      ease(this.wind.gain.gain, m.wind * 0.5 * gust, ctx, 0.2);
      ease(this.wind.filter.frequency, 350 + 900 * m.wind + 150 * Math.sin(this.windPhase * 0.4), ctx, 0.3);
    }
  }

  private traffic(dt: number, cam: THREE.Vector3, cars: readonly SimCar[], alpha: number): void {
    const ctx = this.audio.ctx!;
    const t = ctx.currentTime;
    while (this.voices.length < this.voiceCount) {
      const v = this.makeVoice();
      if (!v) return;
      this.voices.push(v);
    }
    const byId = new Map<number, SimCar>();
    const cand: { id: number; d: number }[] = [];
    for (const c of cars) {
      const d = Math.hypot(c.cur[0] - cam.x, c.cur[1] - cam.y, c.cur[2] - cam.z);
      if (d > VOICE_RANGE) continue;
      byId.set(c.id, c);
      cand.push({ id: c.id, d });
    }
    const active = this.voices.slice(0, this.voiceCount);
    for (const v of this.voices.slice(this.voiceCount)) {
      ease(v.out.gain, 0, ctx, 0.05);
      v.car = null;
    }
    const want = assignVoices(
      active.map((v) => v.pending ?? v.car),
      cand,
    );
    active.forEach((v, i) => {
      const id = want[i];
      if (id !== (v.pending ?? v.car)) {
        // Fade out before moving to the new car (no jump in position or pitch).
        v.pending = id;
        v.switching = 0.12;
        ease(v.out.gain, 0, ctx, 0.03);
      }
      if (v.switching > 0) {
        v.switching -= dt;
        if (v.switching > 0) return;
        v.car = v.pending;
        v.pending = null;
        v.lastDist = -1;
      }
      const c = v.car !== null ? byId.get(v.car) : undefined;
      if (!c) {
        ease(v.out.gain, 0, ctx, 0.05);
        return;
      }
      const x = c.prev[0] + (c.cur[0] - c.prev[0]) * alpha;
      const y = c.prev[1] + (c.cur[1] - c.prev[1]) * alpha + 0.6;
      const z = c.prev[2] + (c.cur[2] - c.prev[2]) * alpha;
      const d = Math.hypot(x - cam.x, y - cam.y, z - cam.z);
      if (v.lastDist < 0) {
        v.panner.positionX.setValueAtTime(x, t);
        v.panner.positionY.setValueAtTime(y, t);
        v.panner.positionZ.setValueAtTime(z, t);
        v.closing = 0;
        v.lastV = c.v;
        v.accel = 0;
      } else {
        v.panner.positionX.setTargetAtTime(x, t, 0.02);
        v.panner.positionY.setTargetAtTime(y, t, 0.02);
        v.panner.positionZ.setTargetAtTime(z, t, 0.02);
        v.closing += ((v.lastDist - d) / Math.max(dt, 1e-3) - v.closing) * Math.min(1, dt * 6);
        v.accel += ((c.v - v.lastV) / Math.max(dt, 1e-3) - v.accel) * Math.min(1, dt * 4);
      }
      v.lastDist = d;
      v.lastV = c.v;
      const pitch = 0.85 + hashId(c.id) * 0.3;
      const push = Math.max(0, Math.min(1, v.accel / 1.6));
      const rate = (pitch * (0.78 + c.v * 0.055 + push * 0.12)) * doppler(v.closing);
      ease(v.engine.src.playbackRate, rate, ctx, 0.05);
      ease(v.engineFilter.frequency, 700 + c.v * 110 + push * 900, ctx, 0.1);
      ease(v.engine.gain.gain, 0.45 + 0.35 * push, ctx, 0.1);
      const roll = Math.min(1, c.v / 14);
      ease(v.tyre.gain, 0.55 * Math.pow(roll, 1.5), ctx, 0.1);
      ease(v.tyreFilter.frequency, (380 + c.v * 45) * doppler(v.closing), ctx, 0.1);
      ease(v.out.gain, 1.3, ctx, 0.06);
    });
  }

  /** Cars honk at a player blocking them, and now and then someone toots anyway. */
  private horns(dt: number, cam: THREE.Vector3, cars: readonly SimCar[]): void {
    for (const c of cars) {
      if (c.blocked < 1.2) {
        if (c.blocked === 0) this.hornAt.delete(c.id);
        continue;
      }
      const at = this.hornAt.get(c.id) ?? 0;
      if (this.time < at) continue;
      const d = Math.hypot(c.cur[0] - cam.x, c.cur[2] - cam.z);
      if (d > 90) continue;
      const long = c.blocked > 6;
      this.hornAt.set(c.id, this.time + (long ? 2.5 : 3) + hashId(c.id + 7) * 3);
      this.honk(c, long ? 0.7 + hashId(c.id) * 0.5 : 0.2, long ? 1 : 2);
    }
    this.nextToot -= dt;
    if (this.nextToot <= 0) {
      this.nextToot = 25 + Math.random() * 45;
      const waiting = cars.filter((c) => c.v < 0.5 && Math.hypot(c.cur[0] - cam.x, c.cur[2] - cam.z) < 150);
      const c = waiting[Math.floor(Math.random() * waiting.length)];
      if (c) this.honk(c, 0.18, Math.random() < 0.5 ? 1 : 2);
    }
    // Forget cars that are gone.
    if (this.hornAt.size > 64) for (const id of this.hornAt.keys()) if (!cars.some((c) => c.id === id)) this.hornAt.delete(id);
  }

  private honk(c: SimCar, dur: number, beeps: number): void {
    const a = this.audio;
    const ctx = a.ctx!;
    const p = a.panner(8, 1.1);
    if (!p) return;
    p.positionX.value = c.cur[0];
    p.positionY.value = c.cur[1] + 0.8;
    p.positionZ.value = c.cur[2];
    p.connect(a.outdoor);
    p.connect(a.streetSend);
    horn(ctx, p, ctx.currentTime + 0.02, Math.max(0.12, dur), 0.8 + hashId(c.id) * 0.45, 0.5, beeps);
    setTimeout(() => p.disconnect(), (dur * beeps + 1) * 1000);
  }

  /** A siren somewhere across the city every minute or two, drifting past behind the buildings. */
  private sirens(dt: number, cam: THREE.Vector3): void {
    const a = this.audio;
    const t = a.ctx!.currentTime;
    const s = this.siren;
    if (s) {
      s.angle += (dt * 9) / s.dist;
      s.panner.positionX.setTargetAtTime(cam.x + Math.cos(s.angle) * s.dist, t, 0.1);
      s.panner.positionY.setTargetAtTime(cam.y, t, 0.1);
      s.panner.positionZ.setTargetAtTime(cam.z + Math.sin(s.angle) * s.dist, t, 0.1);
      if (this.time > s.until) {
        s.panner.disconnect();
        this.siren = null;
      }
      return;
    }
    this.nextSiren -= dt;
    if (this.nextSiren > 0) return;
    this.nextSiren = 70 + Math.random() * 110;
    const name = Math.random() < 0.5 ? 'siren0' : 'siren1';
    const panner = a.panner(160, 1);
    if (!panner) return;
    const angle = Math.random() * Math.PI * 2;
    const dist = 220 + Math.random() * 200;
    panner.positionX.value = cam.x + Math.cos(angle) * dist;
    panner.positionY.value = cam.y;
    panner.positionZ.value = cam.z + Math.sin(angle) * dist;
    panner.connect(a.outdoor);
    panner.connect(a.streetSend);
    const src = a.shot(name, panner, 0.3);
    if (!src) return panner.disconnect();
    this.siren = { panner, angle, dist, until: this.time + a.duration(name) + 0.5 };
  }

  private playerCar(me: PlayerAudio): void {
    const car = me.car;
    if (!car) {
      this.dropPlayerCar();
      return;
    }
    this.player ??= this.makePlayerCar();
    const p = this.player;
    if (!p) return;
    const ctx = this.audio.ctx!;
    const t = ctx.currentTime;
    p.panner.positionX.setTargetAtTime(car.position.x, t, 0.02);
    p.panner.positionY.setTargetAtTime(car.position.y + 0.5, t, 0.02);
    p.panner.positionZ.setTargetAtTime(car.position.z, t, 0.02);
    const gb = this.gearbox;
    const load = gb.load;
    // Pitch follows the revs; the loop is stretched less at the extremes to keep it natural.
    const rate = Math.max(0.5, Math.min(2.7, Math.pow(gb.rpm / ENGINE_RPM, 0.85)));
    ease(p.engine.src.playbackRate, rate, ctx, 0.03);
    ease(p.engine.gain.gain, 0.5 + 0.35 * load + 0.15 * gb.rev, ctx, 0.05);
    ease(p.filter.frequency, 900 + 2600 * load + 1800 * gb.rev, ctx, 0.05);
    const firing = gb.rpm / 30;
    ease(p.growl.frequency, firing, ctx, 0.03);
    ease(p.growlSub.frequency, firing / 2, ctx, 0.03);
    ease(p.growlFilter.frequency, 180 + 700 * load + firing * 1.5, ctx, 0.05);
    ease(p.growlGain.gain, 0.05 * load * (0.4 + gb.rev), ctx, 0.05);
    // Road roar and rumble off the road.
    const v = Math.abs(car.speed);
    const ground = car.wheels > 0 ? 1 : 0;
    ease(p.road.gain, ground * Math.min(0.5, Math.pow(v / 30, 1.2) * 0.45), ctx, 0.1);
    ease(p.roadFilter.frequency, 180 + v * 22, ctx, 0.1);
    // Tyres squeal when the car slides or the handbrake locks the rear at speed.
    const skid = ground * Math.min(1, Math.max(0, (Math.abs(car.lateral) - 2.2) / 5) + (car.handbrake && v > 5 ? 0.45 : 0));
    ease(p.squeal.gain.gain, skid * 0.55, ctx, skid > 0 ? 0.04 : 0.12);
    ease(p.squeal.src.playbackRate, 0.92 + skid * 0.14, ctx, 0.1);
    // Crashes.
    const hit = car.impact;
    if (hit > 1.4 && this.time - this.lastHit > 0.15) {
      this.lastHit = this.time;
      const a = this.audio;
      const pick = (base: string) => `${base}${Math.floor(Math.random() * 5)}`;
      const k = Math.min(1, hit / 12);
      if (hit > 9) a.shot('crash', p.panner, 0.5 + 0.5 * k, 0.95 + Math.random() * 0.1);
      if (hit > 12) a.shot(pick('glass'), p.panner, 0.5);
      a.shot(pick(hit > 5 ? 'hit_heavy' : hit > 2.5 ? 'hit_medium' : 'hit_light'), p.panner, 0.35 + 0.65 * k, 0.8 + Math.random() * 0.2);
    }
  }

  /** A car door: a dull metal thunk. */
  door(): void {
    const a = this.audio;
    if (!a.ready) return;
    a.shot(`hit_medium${Math.floor(Math.random() * 5)}`, a.direct, 0.45, 0.62);
    a.shot(`hit_light${Math.floor(Math.random() * 5)}`, a.direct, 0.25, 0.8, (a.ctx?.currentTime ?? 0) + 0.02);
  }

  private footsteps(dt: number, me: PlayerAudio): void {
    const w = me.walker;
    if (!w) {
      this.lastFeet = null;
      return;
    }
    const moved = this.lastFeet ? Math.hypot(w.feet.x - this.lastFeet.x, w.feet.z - this.lastFeet.z) : 0;
    this.lastFeet = w.feet.clone();
    const ev = this.strides.update(dt, moved, w.grounded, w.running);
    if (!ev) return;
    const g = me.ground(w.feet);
    const surf = stepSurface(g?.tag, g?.y ?? w.feet.y, Math.floor(w.feet.y / 3));
    const a = this.audio;
    const play = (gain: number, rate: number, delay = 0) => {
      // A variation other than the last one on this surface.
      const last = this.lastStep.get(surf) ?? -1;
      let i = Math.floor(Math.random() * (last < 0 ? 5 : 4));
      if (last >= 0 && i >= last) i++;
      this.lastStep.set(surf, i);
      a.shot(`step_${surf}${i}`, a.direct, gain, rate, a.ctx!.currentTime + delay);
    };
    const base = surf === 'grass' || surf === 'snow' ? 1 : 0.75;
    if (ev === 'land') {
      play(base * 1.3, 0.85);
      play(base * 1.1, 0.9, 0.045);
    } else play(base * (w.running ? 1.25 : 1), 0.92 + Math.random() * 0.16);
  }
}
